// DESCRIPTION: List view — events grouped by date in a chronological scrollable list.

import React, { useState } from 'react';
import EventPopover from './EventPopover';
import { formatTime, getDisplayTime } from './utils';

const config = window.nemtlyCalendar || {};
const strings = config.strings || {};

/**
 * Group events by their local date string.
 *
 * @param {Array}  events
 * @param {string} dateFrom ISO date string — first date to include.
 * @param {string} dateTo   ISO date string — last date to include.
 * @return {Array} Array of { dateStr, events[] }, sorted ascending.
 */
function groupByDate( events, dateFrom, dateTo ) {
	const map = {};
	events.forEach( ( event ) => {
		const key = event.date_utc || event.date || '';
		if ( key < dateFrom || key > dateTo ) {
			return;
		}
		if ( ! map[ key ] ) {
			map[ key ] = [];
		}
		map[ key ].push( event );
	} );

	return Object.keys( map )
		.sort()
		.map( ( dateStr ) => ( { dateStr, events: map[ dateStr ] } ) );
}

function formatDateHeading( dateStr ) {
	const d = new Date( dateStr + 'T00:00:00' );
	return d.toLocaleDateString( undefined, {
		weekday: 'long',
		year: 'numeric',
		month: 'long',
		day: 'numeric',
	} );
}

/**
 * ListView
 *
 * @param {Object}  props
 * @param {Array}   props.events
 * @param {string}  props.dateFrom
 * @param {string}  props.dateTo
 * @param {boolean} props.useLocalTz Whether to show times in browser's local timezone.
 */
export default function ListView( { events, dateFrom, dateTo, useLocalTz } ) {
	const [ activeEvent, setActiveEvent ] = useState( null );
	const groups = groupByDate( events, dateFrom, dateTo );

	if ( groups.length === 0 ) {
		return (
			<p className="nb-calendar-error">
				{ strings.noEvents || 'No events this period.' }
			</p>
		);
	}

	const eventUrl = ( event ) =>
		event.slug ? `${ window.location.origin }/${ event.slug }/` : '#';

	return (
		<div className="nb-calendar-list">
			{ groups.map( ( { dateStr, events: dayEvents } ) => (
				<div key={ dateStr } className="nb-calendar-list-day">
					<div className="nb-calendar-list-day-heading">
						{ formatDateHeading( dateStr ) }
					</div>

					{ dayEvents.map( ( event ) => (
						<div
							key={ event.id }
							className={ `nb-calendar-list-event${
								event.is_past
									? ' nb-calendar-list-event--past'
									: ''
							}` }
							style={ { position: 'relative' } }
							onClick={ () =>
								setActiveEvent(
									activeEvent?.id === event.id ? null : event
								)
							}
							role="button"
							tabIndex={ 0 }
							onKeyDown={ ( e ) =>
								e.key === 'Enter' && setActiveEvent( event )
							}
							aria-label={ event.title }
						>
							<div className="nb-calendar-list-event__time">
								{ formatTime(
									getDisplayTime( event, useLocalTz )
								) || '–' }
							</div>

							<div className="nb-calendar-list-event__title">
								{ event.title }
							</div>

							{ event.location && (
								<div className="nb-calendar-list-event__location">
									{ event.location }
								</div>
							) }

							{ config.showBookingBtn !== false &&
								event.is_bookable && (
									<a
										href={ eventUrl( event ) }
										className="nb-calendar-list-event__book-btn"
										onClick={ ( e ) => e.stopPropagation() }
									>
										{ strings.bookNow || 'Book Now' }
									</a>
								) }

							{ activeEvent?.id === event.id && (
								<EventPopover
									event={ event }
									useLocalTz={ useLocalTz }
									onClose={ () => setActiveEvent( null ) }
								/>
							) }
						</div>
					) ) }
				</div>
			) ) }
		</div>
	);
}
